import { supabase } from './supabaseService';
import { logger } from '../utils/logger';


interface GrafanaConfig {
  enabled: boolean;
  url: string;
  apiKey: string;
  lokiUrl?: string;
  dashboardId?: string;
}

interface Metric {
  name: string;
  value: number;
  timestamp: number;
  labels: Record<string, string>;
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  labels: Record<string, string>;
  metadata?: Record<string, any>;
}

class GrafanaMetricsService {
  private config: GrafanaConfig | null = null;
  private metricsQueue: Metric[] = [];
  private logsQueue: LogEntry[] = [];
  private isProcessing: boolean = false;
  private readonly BATCH_SIZE = 50;
  private readonly FLUSH_INTERVAL = 10000; // 10 seconds
  private flushTimer: number | null = null;

  constructor() {
    this.loadConfig();

    if (typeof window !== 'undefined') {
      this.startAutoFlush();
    }
  }

  private loadConfig(): void {
    try {
      const config = localStorage.getItem('crm_configuration');
      if (config) {
        const parsed = JSON.parse(config);
        this.config = {
          enabled: parsed.grafanaEnabled === true,
          url: parsed.grafanaUrl || '',
          apiKey: parsed.grafanaApiKey || '',
          lokiUrl: parsed.grafanaLokiUrl || '',
          dashboardId: parsed.grafanaDashboardId || ''
        };

        logger.debug('Grafana config loaded', {
          enabled: this.config.enabled,
          hasUrl: !!this.config.url,
          hasApiKey: !!this.config.apiKey
        });
      }
    } catch (error) {
      logger.error('Error loading Grafana config', error);
    }
  }

  updateConfig(config: Partial<GrafanaConfig>): void {
    this.config = {
      ...this.config,
      ...config
    } as GrafanaConfig;

    // Save to localStorage
    try {
      const stored = localStorage.getItem('crm_configuration') || '{}';
      const current = JSON.parse(stored);
      const updated = {
        ...current,
        grafanaEnabled: this.config.enabled,
        grafanaUrl: this.config.url,
        grafanaApiKey: this.config.apiKey,
        grafanaLokiUrl: this.config.lokiUrl,
        grafanaDashboardId: this.config.dashboardId
      };
      localStorage.setItem('crm_configuration', JSON.stringify(updated));
      logger.info('Grafana config updated');
    } catch (error) {
      logger.error('Error saving Grafana config', error);
    }
  }

  isEnabled(): boolean {
    return this.config?.enabled === true;
  }

  async sendMetric(metric: Metric): Promise<void> {
    if (!this.isEnabled()) return;

    this.metricsQueue.push(metric);

    if (this.metricsQueue.length >= this.BATCH_SIZE) {
      await this.flush();
    }
  }

  async sendLog(log: LogEntry): Promise<void> {
    if (!this.isEnabled()) return;

    this.logsQueue.push(log);

    if (this.logsQueue.length >= this.BATCH_SIZE) {
      await this.flush();
    }
  }

  async trackAuthEvent(eventType: string, userId: string, metadata: Record<string, any> = {}): Promise<void> {
    if (!this.isEnabled()) return;

    const metric: Metric = {
      name: 'crm_auth_events_total',
      value: 1,
      timestamp: Date.now(),
      labels: {
        event_type: eventType,
        user_id: userId,
        ...metadata
      }
    };

    await this.sendMetric(metric);

    const log: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'info',
      message: `Auth event: ${eventType}`,
      labels: {
        event_type: eventType,
        user_id: userId
      },
      metadata
    };

    await this.sendLog(log);
  }

  async trackSessionIntegrityViolation(
    expectedUserId: string,
    receivedUserId: string,
    reason: string
  ): Promise<void> {
    if (!this.isEnabled()) return;

    const metric: Metric = {
      name: 'crm_session_integrity_violations_total',
      value: 1,
      timestamp: Date.now(),
      labels: {
        expected_user: expectedUserId,
        received_user: receivedUserId,
        reason
      }
    };

    await this.sendMetric(metric);

    const log: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'critical',
      message: `Session integrity violation: ${reason}`,
      labels: {
        expected_user: expectedUserId,
        received_user: receivedUserId
      },
      metadata: { reason }
    };

    await this.sendLog(log);
  }

  async trackDeployment(previousVersion: string, newVersion: string, userId?: string): Promise<void> {
    if (!this.isEnabled()) return;

    const metric: Metric = {
      name: 'crm_deployments_total',
      value: 1,
      timestamp: Date.now(),
      labels: {
        previous_version: previousVersion,
        new_version: newVersion,
        ...(userId && { user_id: userId })
      }
    };

    await this.sendMetric(metric);

    const log: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'info',
      message: `Deployment detected: ${previousVersion} → ${newVersion}`,
      labels: {
        previous_version: previousVersion,
        new_version: newVersion
      },
      metadata: { userId }
    };

    await this.sendLog(log);
  }

  async testConnection(): Promise<{ success: boolean; message: string }> {
    if (!this.config || !this.config.enabled) {
      return { success: false, message: 'Grafana no está configurado o habilitado' };
    }

    try {
      const { data, error } = await supabase.functions.invoke('send-metrics-to-grafana', {
        body: {
          type: 'test',
          timestamp: new Date().toISOString()
        }
      });

      if (error) {
        return { success: false, message: `Error: ${error.message}` };
      }

      if (data?.success) {
        return { success: true, message: 'Conexión exitosa con Grafana' };
      }

      return { success: false, message: 'Respuesta inesperada del servidor' };
    } catch (error) {
      return {
        success: false,
        message: `Error de conexión: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  async getMetricsStats(): Promise<{
    queuedMetrics: number;
    queuedLogs: number;
    isProcessing: boolean;
    enabled: boolean;
  }> {
    return {
      queuedMetrics: this.metricsQueue.length,
      queuedLogs: this.logsQueue.length,
      isProcessing: this.isProcessing,
      enabled: this.isEnabled()
    };
  }

  private async flush(): Promise<void> {
    if (this.isProcessing || (!this.metricsQueue.length && !this.logsQueue.length)) {
      return;
    }

    if (!this.isEnabled()) {
      // Clear queues if disabled
      this.metricsQueue = [];
      this.logsQueue = [];
      return;
    }

    this.isProcessing = true;

    try {
      const metricsBatch = this.metricsQueue.splice(0, this.BATCH_SIZE);
      const logsBatch = this.logsQueue.splice(0, this.BATCH_SIZE);

      if (metricsBatch.length > 0 || logsBatch.length > 0) {
        await this.sendToGrafana(metricsBatch, logsBatch);
      }
    } catch (error) {
      logger.error('Error flushing Grafana queue', error);
    } finally {
      this.isProcessing = false;
    }
  }

  private async sendToGrafana(metrics: Metric[], logs: LogEntry[]): Promise<void> {
    try {
      const { error } = await supabase.functions.invoke('send-metrics-to-grafana', {
        body: {
          type: 'batch',
          metrics,
          logs,
          timestamp: new Date().toISOString()
        }
      });

      if (error) {
        logger.error('Error sending to Grafana', error);
        // Re-queue failed items
        this.metricsQueue.unshift(...metrics);
        this.logsQueue.unshift(...logs);
      } else {
        logger.info('Sent to Grafana', metrics.length, 'metrics,', logs.length, 'logs');
      }
    } catch (error) {
      logger.error('Failed to send to Grafana', error);
      // Re-queue failed items
      this.metricsQueue.unshift(...metrics);
      this.logsQueue.unshift(...logs);
    }
  }

  private startAutoFlush(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = window.setInterval(() => {
      if (this.metricsQueue.length > 0 || this.logsQueue.length > 0) {
        this.flush();
      }
    }, this.FLUSH_INTERVAL);
  }

  async forceFlush(): Promise<void> {
    await this.flush();
  }

  stopAutoFlush(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }
}

export const grafanaMetricsService = new GrafanaMetricsService();
export default grafanaMetricsService;
